// Filas de "Por vendedor": el agregado por vendedor con su nombre, su tienda de base y si es encargado.
import type { RetailCommission } from '@fiado/type-kit';
import type { CommissionDirectory } from '../api/useCommissionDirectory';

export interface SellerRow {
    sellerId: string;
    name: string;
    storeId: string | null;
    storeName: string;
    manager: boolean;
    salesCount: number;
    placementCents: number;
    paidCents: number;
}

export type SellerPill = 'all' | 'with' | 'without';
export type SellerRole = 'all' | 'manager' | 'seller';
export type SellerSortKey = 'name' | 'store' | 'sales' | 'placement' | 'paid';

export interface SellerSort {
    key: SellerSortKey;
    direction: 'asc' | 'desc';
}

type Directory = Pick<
    CommissionDirectory,
    'sellerName' | 'sellerStoreId' | 'storeName' | 'isStoreManager'
>;

export function buildSellerRows(
    rows: readonly RetailCommission.CommissionAggregateRow[],
    directory: Directory,
): SellerRow[] {
    return rows.map((row) => {
        const storeId = directory.sellerStoreId(row.key);
        return {
            sellerId: row.key,
            name: directory.sellerName(row.key),
            storeId,
            storeName: storeId ? directory.storeName(storeId) : '—',
            manager: directory.isStoreManager(row.key),
            salesCount: row.salesCount,
            placementCents: row.placementCents,
            paidCents: row.paidCents,
        };
    });
}

export function filterSellerRows(
    rows: readonly SellerRow[],
    filters: { search: string; storeId: string; role: SellerRole },
): SellerRow[] {
    const query = filters.search.trim().toLowerCase();
    return rows.filter(
        (row) =>
            (query === '' ||
                row.name.toLowerCase().includes(query) ||
                row.sellerId.toLowerCase().includes(query)) &&
            (filters.storeId === '' || row.storeId === filters.storeId) &&
            (filters.role === 'all' || (filters.role === 'manager') === row.manager),
    );
}

/** Los conteos salen de lo filtrado, antes de aplicar la pill. */
export function pillCounts(rows: readonly SellerRow[]): Record<SellerPill, number> {
    const withSales = rows.filter((row) => row.salesCount > 0).length;
    return { all: rows.length, with: withSales, without: rows.length - withSales };
}

export function applyPill(rows: readonly SellerRow[], pill: SellerPill): SellerRow[] {
    if (pill === 'all') return [...rows];
    return rows.filter((row) => (pill === 'with' ? row.salesCount > 0 : row.salesCount === 0));
}

const SORT_VALUE: Record<SellerSortKey, (row: SellerRow) => string | number> = {
    name: (row) => row.name,
    store: (row) => row.storeName,
    sales: (row) => row.salesCount,
    placement: (row) => row.placementCents,
    paid: (row) => row.paidCents,
};

export function sortSellerRows(rows: readonly SellerRow[], sort: SellerSort): SellerRow[] {
    const value = SORT_VALUE[sort.key];
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const x = value(a);
        const y = value(b);
        return (
            sign *
            (typeof x === 'number' && typeof y === 'number'
                ? x - y
                : String(x).localeCompare(String(y)))
        );
    });
}

/** Nombre y tienda empiezan ascendente; las cifras, descendente. Repetir la columna invierte. */
export function nextSellerSort(current: SellerSort, key: SellerSortKey): SellerSort {
    if (current.key === key)
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    return { key, direction: key === 'name' || key === 'store' ? 'asc' : 'desc' };
}

export function totalsOf(rows: readonly SellerRow[]): {
    salesCount: number;
    placementCents: number;
    paidCents: number;
} {
    return rows.reduce(
        (total, row) => ({
            salesCount: total.salesCount + row.salesCount,
            placementCents: total.placementCents + row.placementCents,
            paidCents: total.paidCents + row.paidCents,
        }),
        { salesCount: 0, placementCents: 0, paidCents: 0 },
    );
}
